import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {
  Bucket,
  DispatchGroup,
  FeeTitle,
  FeeTitleSortableField,
  SearchableDispatchGroupSortableFields,
  SearchableSortDirection,
  Skill,
  useGetDistinctValuesQuery,
  useSearchDispatchGroupsQuery,
  useSearchFeeTitlesQuery,
  useSearchSkillsQuery,
  useSearchDispatchStationGroupsQuery,
  DispatchStationGroup
} from "../generated/graphql";
import {useAuthState} from "./AuthProvider";
import {Constants} from "../constants/constants";
import {DispatchStation} from "../components/common/types";

export type DispatchDataProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

export type DispatchDataState = {
  isDispatchDataLoaded: boolean;
  dispatchStations: DispatchStation[];
  dispatchGroups: DispatchGroup[];
  skills: Skill[];
  services: string[];
  vehicleTypes: string[];
  sites: string[];
  packageTypes: string[];
  users: string[];
  orderRequirements: string[];
};

const initialState: DispatchDataState = {
  isDispatchDataLoaded: false,
  dispatchStations: [],
  dispatchGroups: [],
  skills: [],
  services: [],
  vehicleTypes: [],
  sites: [],
  packageTypes: [],
  users: [],
  orderRequirements: []
};

export const DispatchDataContext = createContext<DispatchDataState>(initialState);

const decorateDistinctData = (data: any): string[] => {
  return data?.getDistinctValues?.items.map((y: Bucket) => y.key!) || [];
};

const processDispatchStations = (data: DispatchStationGroup[]) => {
  return data.reduce((currentDispatchStations, dispatchStation: DispatchStationGroup) => {
    let processingStation;
    if (currentDispatchStations.has(dispatchStation.dispatchStation)) {
      processingStation = currentDispatchStations.get(dispatchStation.dispatchStation)!;
    } else {
      processingStation = {
        stationId: dispatchStation.dispatchStation,
        description: dispatchStation.description ?? dispatchStation.dispatchStation,
        jobGroups: new Set<number>(),
        driverGroups: new Set<number>()
      } as DispatchStation;
    }
    if ("J" === dispatchStation.dispatchType) {
      processingStation.jobGroups.add(Number(dispatchStation.dispatchGroupId));
    }
    if ("D" === dispatchStation.dispatchType) {
      processingStation.driverGroups.add(Number(dispatchStation.dispatchGroupId));
    }
    currentDispatchStations.set(processingStation.stationId, processingStation);
    return currentDispatchStations;
  }, new Map<string, DispatchStation>());
};

const DispatchDataProvider = ({children}: DispatchDataProviderProps) => {
  const [state, setState] = useState<DispatchDataState>(initialState);
  const {isAuthenticated} = useAuthState();

  const {data: stationsData, loading: stationsLoading} = useSearchDispatchStationGroupsQuery({
    variables: {
      filter: {},
      limit: Constants.DISPATCH_STATION_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: dispatchGroupsData, loading: dispatchGroupsDataLoading} = useSearchDispatchGroupsQuery({
    variables: {
      sort: {
        field: SearchableDispatchGroupSortableFields.DispatchGroup
      },
      limit: Constants.DISPATCH_GROUP_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: servicesData, loading: servicesDataLoading} = useGetDistinctValuesQuery({
    variables: {
      field: "service",
      typeName: "jobs",
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: vehicleTypesData, loading: vehicleTypesDataLoading} = useGetDistinctValuesQuery({
    variables: {
      field: "vehicleType.description.keyword",
      typeName: "jobs",
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: sitesData, loading: sitesDataLoading} = useGetDistinctValuesQuery({
    variables: {
      field: "code.keyword",
      typeName: "ng_site",
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: packageTypesData, loading: packageTypesDataLoading} = useGetDistinctValuesQuery({
    variables: {
      field: "description.keyword",
      typeName: "ng_packagetype",
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: usersData, loading: usersDataLoading} = useGetDistinctValuesQuery({
    variables: {
      field: "order.user.userName.keyword",
      typeName: "jobs",
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: skillsData, loading: skillsDataLoading} = useSearchSkillsQuery({
    variables: {
      filter: {
        status: {
          eq: 1 // Active skills only
        }
      },
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const {data: orderRequirementsResult, loading: orderRequirementsLoading} = useSearchFeeTitlesQuery({
    variables: {
      filter: {
        driverRequireFlag: {
          eq: true
        }
      },
      sort: {
        field: FeeTitleSortableField.Name,
        direction: SearchableSortDirection.Asc
      },
      limit: Constants.DISPATCH_DATA_LIMIT
    },
    skip: !isAuthenticated
  });

  const isDataFetchingFinished = useMemo(
    () =>
      !stationsLoading &&
      !dispatchGroupsDataLoading &&
      !servicesDataLoading &&
      !skillsDataLoading &&
      !vehicleTypesDataLoading &&
      !sitesDataLoading &&
      !packageTypesDataLoading &&
      !usersDataLoading &&
      !orderRequirementsLoading,
    [
      stationsLoading,
      dispatchGroupsDataLoading,
      servicesDataLoading,
      skillsDataLoading,
      vehicleTypesDataLoading,
      sitesDataLoading,
      packageTypesDataLoading,
      usersDataLoading,
      orderRequirementsLoading
    ]
  );

  const dispatchGroups = useMemo(
    () => (dispatchGroupsData?.searchDispatchGroups?.items as DispatchGroup[]) || [],
    [dispatchGroupsData]
  );
  const packageTypes = useMemo(() => decorateDistinctData(packageTypesData), [packageTypesData]);
  const services = useMemo(() => decorateDistinctData(servicesData), [servicesData]);
  const vehicleTypes = useMemo(() => decorateDistinctData(vehicleTypesData), [vehicleTypesData]);
  const sites = useMemo(() => decorateDistinctData(sitesData), [sitesData]);
  const skills = useMemo(() => skillsData?.searchSkills?.items || [], [skillsData]);
  const users = useMemo(() => decorateDistinctData(usersData), [usersData]);
  const orderRequirements = useMemo(
    () =>
      (orderRequirementsResult?.searchFeeTitles?.items || [])
        .map((y: FeeTitle) => y.name || "")
        .filter((name) => !!name),
    [orderRequirementsResult]
  );

  const dispatchStations = useMemo(
    () =>
      Array.from(
        processDispatchStations(
          (stationsData?.searchDispatchStationGroups?.items || []) as DispatchStationGroup[]
        ).values()
      ),
    [stationsData]
  );

  useEffect(() => {
    if (!isDataFetchingFinished) return;

    setState((prevState) => {
      return {
        ...prevState,
        dispatchGroups,
        skills,
        services,
        vehicleTypes,
        sites,
        packageTypes,
        users,
        orderRequirements,
        dispatchStations,
        isDispatchDataLoaded: true
      };
    });
  }, [
    skills,
    isDataFetchingFinished,
    services,
    vehicleTypes,
    sites,
    packageTypes,
    users,
    orderRequirements,
    dispatchGroups,
    dispatchStations
  ]);

  return <DispatchDataContext.Provider value={state}>{children}</DispatchDataContext.Provider>;
};

export const useDispatchDataContext = () => {
  return useContext(DispatchDataContext);
};

export default DispatchDataProvider;
